import type { TestContext } from 'yup';

const NON_NEGATIVE_INTEGER_PATTERN = /^\d+$/;

/**
 * `yup.number()` schemas cast values through `Number()`, which silently loses precision for
 * integers beyond `Number.MAX_SAFE_INTEGER` (~9e15). When a schema's max bound sits well beyond
 * that range, comparing the already-cast value against it can wrongly accept inputs that
 * actually exceed the limit, because distinct large inputs round to the same float.
 *
 * Use this as a `yup.test()` predicate to compare the raw, pre-cast input (yup's
 * `originalValue`) via `BigInt` instead, so the check stays precise for very large integers.
 * This only recovers precision when the raw input reaches yup as a string (e.g. a text form
 * field) - if the caller already constructed a JS `number` beyond the safe integer range,
 * precision is lost before yup ever sees it and cannot be recovered here.
 *
 * Falls back to a plain numeric comparison for values that aren't a non-negative integer
 * string (e.g. decimals, negative numbers, `NaN`), since those are expected to be rejected by
 * other schema checks (`typeError`, `integer`, `positive`) instead.
 *
 * @param max - The inclusive maximum allowed value.
 */
export function isWithinBigIntMax(max: number) {
  return function (this: TestContext, value: number | null | undefined) {
    if (value == null) {
      return true;
    }

    const rawValue = this.originalValue;
    const rawDigits = typeof rawValue === 'number' ? rawValue.toFixed(0) : String(rawValue ?? '').trim();

    if (!NON_NEGATIVE_INTEGER_PATTERN.test(rawDigits)) {
      return value <= max;
    }

    return BigInt(rawDigits) <= BigInt(max);
  };
}
